import React, { useState, useEffect, memo } from 'react'
import Modal from '@cloudscape-design/components/modal'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Button from '@cloudscape-design/components/button'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Alert from '@cloudscape-design/components/alert'
import { sanitizeText, isValidAWSUserid, isValidApprovalId } from '../../utils/validation'

/**
 * EditDocumentDialog component for editing or deleting a document
 * Accessible to librarians (full access) and document owners (edit only)
 */
const EditDocumentDialog = memo(({ 
  isOpen, 
  onClose, 
  onUpdate, 
  onDelete,
  document,
  isProcessing,
  isLibrarian = false
}) => {
  const [formData, setFormData] = useState({
    did: '',
    title: '',
    location: '',
    approvalId: '',
    owner: '',
    status: ''
  })
  
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Initialize form with document data when dialog opens
  useEffect(() => {
    if (isOpen && document) {
      setFormData({
        did: document.did || '',
        title: document.title || '',
        location: document.location || '',
        approvalId: document.approvalId || '',
        owner: document.owner || '',
        status: document.status || ''
      })
      setErrors({})
      setTouched({})
      setShowDeleteConfirm(false)
    }
  }, [isOpen, document])

  // Validate form
  useEffect(() => {
    const newErrors = {}
    
    if (touched.location && formData.location && formData.location.trim() !== '') {
      try {
        const url = new URL(formData.location)
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          newErrors.location = 'URL must use http or https protocol'
        }
      } catch {
        newErrors.location = 'Invalid URL format'
      }
    }
    
    if (touched.owner) {
      if (!formData.owner || formData.owner.trim() === '') {
        newErrors.owner = 'Owner is required'
      } else if (!isValidAWSUserid(formData.owner)) {
        newErrors.owner = 'Must be a valid AWS userid (lowercase letters, numbers, .-_ only)'
      }
    }

    if (touched.approvalId && formData.approvalId && formData.approvalId.trim() !== '') {
      if (!isValidApprovalId(formData.approvalId)) {
        newErrors.approvalId = 'Must be numeric only, between 6 and 10 digits'
      }
    }
    
    setErrors(newErrors)
  }, [formData, touched])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleUpdate = () => {
    setTouched({
      did: true,
      title: true,
      location: true,
      approvalId: true,
      owner: true,
      status: true
    })

    if (!formData.owner) {
      return
    }

    if (Object.keys(errors).length > 0) {
      return
    }

    const sanitizedData = {
      did: sanitizeText(formData.did),
      owner: sanitizeText(formData.owner),
      status: sanitizeText(formData.status),
      approvalId: sanitizeText(formData.approvalId) || ''
    }

    if (formData.title && formData.title.trim()) {
      sanitizedData.title = sanitizeText(formData.title)
    } else {
      sanitizedData.title = ''
    }
    
    if (formData.location && formData.location.trim()) {
      sanitizedData.location = sanitizeText(formData.location)
    } else {
      sanitizedData.location = ''
    }

    onUpdate(sanitizedData)
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    onDelete(formData.did)
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
  }

  const handleClose = () => {
    if (!isProcessing) {
      setFormData({
        did: '',
        title: '',
        location: '',
        approvalId: '',
        owner: '',
        status: ''
      })
      setErrors({})
      setTouched({})
      setShowDeleteConfirm(false)
      onClose()
    }
  }

  const isFormValid = Object.keys(errors).length === 0 && formData.owner

  return (
    <Modal
      visible={isOpen}
      onDismiss={handleClose}
      header="Edit Document ID"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {!showDeleteConfirm && isLibrarian && (
              <Button onClick={handleDeleteClick} disabled={isProcessing}>
                Delete
              </Button>
            )}
            <Button variant="link" onClick={handleClose} disabled={isProcessing}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleUpdate} 
              disabled={!isFormValid || isProcessing || showDeleteConfirm}
            >
              {isProcessing ? 'Updating...' : 'Update Document'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {showDeleteConfirm && (
          <Alert
            type="error"
            header="Confirm deletion"
            action={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={handleDeleteCancel} disabled={isProcessing}>
                  Cancel
                </Button>
                <Button onClick={handleDeleteConfirm} disabled={isProcessing}>
                  {isProcessing ? 'Deleting...' : 'Confirm Delete'}
                </Button>
              </SpaceBetween>
            }
          >
            Are you sure you want to delete this document? This action cannot be undone.
            <Box variant="p" padding={{ top: 's' }}>
              DID: <strong>{formData.did}</strong>
            </Box>
          </Alert>
        )}

        <FormField
          label="DID"
          constraintText="DID cannot be changed (primary key)"
        >
          <Input
            value={formData.did}
            disabled={true}
            readOnly={true}
          />
        </FormField>

        <FormField label="Title">
          <Input
            value={formData.title}
            onChange={({ detail }) => handleChange('title', detail.value)}
            placeholder="Document title"
            disabled={isProcessing}
          />
        </FormField>

        <FormField
          label="Authoritative URL"
          errorText={errors.location}
        >
          <Input
            value={formData.location}
            onChange={({ detail }) => handleChange('location', detail.value)}
            placeholder="https://example.com/document.pdf"
            type="url"
            disabled={isProcessing}
            invalid={!!errors.location}
          />
        </FormField>

        <FormField
          label="Approval ID"
          errorText={errors.approvalId}
          constraintText="Optional. Numeric only, 6-10 digits."
        >
          <Input
            value={formData.approvalId}
            onChange={({ detail }) => handleChange('approvalId', detail.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g., 12345678"
            disabled={isProcessing}
            invalid={!!errors.approvalId}
            inputMode="numeric"
          />
        </FormField>

        <FormField
          label="Owner"
          errorText={errors.owner}
          constraintText="Required. Enter a valid AWS userid."
        >
          <Input
            value={formData.owner}
            onChange={({ detail }) => handleChange('owner', detail.value)}
            placeholder="e.g., jdoe or john-doe"
            disabled={isProcessing}
            invalid={!!errors.owner}
          />
        </FormField>

        <FormField label="Status">
          <Input
            value={formData.status}
            onChange={({ detail }) => handleChange('status', detail.value)}
            placeholder="e.g., Draft, Current, Archived"
            disabled={isProcessing}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  )
})

EditDocumentDialog.displayName = 'EditDocumentDialog'

export default EditDocumentDialog
